import { useState, useEffect } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

// Types for address components
export interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface FormattedAddress {
  formattedAddress: string;
  streetNumber: string;
  streetName: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  countryCode: string;
  latitude?: number;
  longitude?: number;
}

// Session token for billing optimization
let sessionToken: google.maps.places.AutocompleteSessionToken | undefined;

// Cached Google Maps API client
let placesApiClient: google.maps.PlacesLibrary | undefined;

// Initialize Google Maps API
async function getGoogleMapsPlacesApiClient(): Promise<google.maps.PlacesLibrary> {
  if (placesApiClient) {
    return placesApiClient;
  }

  // Note: In production, this API key should be set in environment variables
  // For now, we'll use a placeholder that needs to be configured
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY';
  
  if (apiKey === 'YOUR_GOOGLE_MAPS_API_KEY') {
    console.warn('Google Maps API key not configured. Please set VITE_GOOGLE_MAPS_API_KEY in your environment variables.');
  }

  const loader = new Loader({
    apiKey,
    version: 'weekly',
    libraries: ['places']
  });

  placesApiClient = await loader.importLibrary('places') as google.maps.PlacesLibrary;
  return placesApiClient;
}

// Helper function to extract address components
function pickAddressComponentByType(
  components: AddressComponent[],
  types: string[]
): string {
  const component = components.find(comp =>
    comp.types.some(type => types.includes(type))
  );
  return component?.long_name || '';
}

// Serialize geocoder result into structured format
export function serializeGeocoderResult(
  result: google.maps.GeocoderResult
): FormattedAddress {
  const components = result.address_components;
  
  return {
    formattedAddress: result.formatted_address,
    streetNumber: pickAddressComponentByType(components, ['street_number']),
    streetName: pickAddressComponentByType(components, ['route', 'street_address']),
    city: pickAddressComponentByType(components, [
      'locality',
      'postal_town',
      'administrative_area_level_2'
    ]),
    state: pickAddressComponentByType(components, ['administrative_area_level_1']),
    postalCode: pickAddressComponentByType(components, ['postal_code']),
    country: pickAddressComponentByType(components, ['country']),
    countryCode: pickAddressComponentByType(components, ['country']),
    latitude: result.geometry?.location?.lat(),
    longitude: result.geometry?.location?.lng(),
  };
}

// Filter predictions to only include relevant address types
export function filterPredictions(
  predictions: google.maps.places.PlacePrediction[]
): google.maps.places.PlacePrediction[] {
  return predictions.filter(({ types }) => {
    // Keep predictions that are street addresses or postal codes
    if (types.includes('street_address') || types.includes('postal_code')) {
      return true;
    }
    // For geocode types, check for building numbers (premise/subpremise)
    if (types.includes('geocode')) {
      return types.some(type => ['premise', 'subpremise'].includes(type));
    }
    return false;
  });
}

export function useGoogleMapsApi() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize the API
  useEffect(() => {
    getGoogleMapsPlacesApiClient()
      .then(() => {
        setIsLoaded(true);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to load Google Maps API:', err);
        setError('Failed to load Google Maps API');
      });
  }, []);

  // Search for place predictions using the new AutocompleteSuggestion API
  const searchPlaces = async (
    query: string,
    options?: {
      languageCode?: string;
      locationBias?: google.maps.places.LocationBias;
      locationRestriction?: google.maps.places.LocationRestriction;
    }
  ): Promise<google.maps.places.PlacePrediction[]> => {
    if (!isLoaded) {
      throw new Error('Google Maps API not loaded yet');
    }

    if (!query || query.trim().length < 3) {
      return [];
    }

    try {
      const places = await getGoogleMapsPlacesApiClient();
      
      if (!sessionToken) {
        sessionToken = new places.AutocompleteSessionToken();
      }

      // Use the new AutocompleteSuggestion API
      const { AutocompleteSuggestion } = places;
      
      const requestOptions: google.maps.places.AutocompleteRequest = {
        input: query.trim(),
        sessionToken,
        // Note: Removed includedPrimaryTypes restriction to get all relevant address results
        // The new API doesn't support 'address' as a type - we'll filter results in filterPredictions instead
      };

      // Add language code if provided
      if (options?.languageCode) {
        requestOptions.language = options.languageCode;
      }

      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(requestOptions);
      
      // Extract PlacePrediction objects from AutocompleteSuggestion
      const predictions = suggestions
        .map(suggestion => suggestion.placePrediction)
        .filter((prediction): prediction is google.maps.places.PlacePrediction => prediction !== null);
      
      // Filter predictions to only include relevant addresses
      const filteredPredictions = filterPredictions(predictions);
      return filteredPredictions;
    } catch (err) {
      console.error('Error searching places:', err);
      return [];
    }
  };

  // Get detailed place information by place ID
  const getPlaceDetails = async (
    placeId: string
  ): Promise<FormattedAddress | null> => {
    if (!isLoaded) {
      throw new Error('Google Maps API not loaded yet');
    }

    try {
      const geocoder = new google.maps.Geocoder();
      
      // Clear session token as it can only be used once
      const currentSessionToken = sessionToken;
      sessionToken = undefined;

      return new Promise((resolve, reject) => {
        geocoder.geocode(
          { 
            placeId,
            ...(currentSessionToken && { sessionToken: currentSessionToken })
          },
          (results, status) => {
            if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
              const formattedResult = serializeGeocoderResult(results[0]);
              resolve(formattedResult);
            } else {
              reject(new Error(`Geocoder error: ${status}`));
            }
          }
        );
      });
    } catch (err) {
      console.error('Error getting place details:', err);
      return null;
    }
  };

  return {
    isLoaded,
    error,
    searchPlaces,
    getPlaceDetails,
  };
}